import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { PlusCircle, X, Send, Paperclip, AlertCircle, Save } from "lucide-react";
import Layout from "@/components/Layout";
import FormLogo from "@/components/FormLogo";
import EmergencyToggle from "@/components/EmergencyToggle";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { SelfSkipSection, isHodDropdownOptional, stripSelfSkipTransients, ResubmitFromSection, computeGrandTotal } from "@/components/FormShell";

function newRow() {
  return { name: "", quantity: "", link: "", cost: "" };
}

export default function NewMaterial() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const draftId = params.get("draft");
  const [depts, setDepts] = useState([]);
  const [hods, setHods] = useState([]);
  const [form, setForm] = useState({
    staffName: user?.name || "",
    staffEmail: user?.email || "",
    staffContact: "",
    mainDept: "",
    subDept: "",
    hodApproval: "", is_hod_self: undefined, is_admin1_self: undefined, admin1_self_cost: "", admin1_self_remark: "",
    materialRequiredBy: "",
    materialCategory: "Hard Product",
    purposeOfUtility: "",
    qualityBrandWarranty: "",
    requiredByDate: "",
    is_emergency: false,
    emergency_reason: "",
    items: [newRow(), newRow(), newRow()],
  });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(draftId || null);
  // Phase 16 (item 10) — Resubmit form state.
  const [isResubmit, setIsResubmit] = useState(false);
  const [parentRefCode, setParentRefCode] = useState(null);

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/hods").then((r) => setHods(r.data)).catch(() => {});
    if (draftId) {
      api.get(`/requisitions/${draftId}`).then((r) => {
        const fd = r.data.form_data || {};
        setForm((f) => ({ ...f, ...fd, items: fd.items?.length ? fd.items : f.items }));
        setAttachments(r.data.attachments || fd.attachments || []);
      }).catch(() => toast.error("Could not load draft"));
    }
  }, [draftId]);

  const subDepts = useMemo(() => {
    const d = depts.find((x) => x.name === form.mainDept);
    return d?.sub_departments || [];
  }, [depts, form.mainDept]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setItem = (i, k, v) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)),
    }));

  const addRow = () => setForm((f) => ({ ...f, items: [...f.items, newRow()] }));
  const rmRow = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const onUpload = async (fieldName, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File larger than 10 MB. Please compress and retry.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((prev) => [
        ...prev.filter((a) => a.field_name !== fieldName),
        { field_name: fieldName, filename: data.filename, url: data.url, size: data.size, uploaded_at: new Date().toISOString() },
      ]);
      toast.success(`${file.name} uploaded.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const validate = () => {
    if (!(form.is_hod_self === true || form.is_admin1_self === true) && !String(form.hodApproval || "").trim()) {
      toast.error("Please select an HOD or declare Yes on Are you a HOD? / Admin-1?");
      return false;
    }
    const req = ["staffName", "staffEmail", "staffContact", "mainDept", "subDept",
                  "materialRequiredBy", "purposeOfUtility", "requiredByDate"];
    for (const k of req) {
      if (!form[k]?.toString().trim()) {
        toast.error(`Please fill: ${k}`);
        return false;
      }
    }
    if (form.is_emergency && (form.emergency_reason || "").trim().length < 10) {
      toast.error("Emergency Reason is mandatory (min 10 characters).");
      return false;
    }
    const filtered = form.items.filter((it) => it.name.trim());
    if (filtered.length === 0) {
      toast.error("Add at least one material row.");
      return false;
    }
    // Phase 4 (fix): qty must be non-empty for every filled row.
    for (const [i, it] of filtered.entries()) {
      if (!String(it.quantity || "").trim()) {
        toast.error(`Quantity is required for material item #${i + 1}.`);
        return false;
      }
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        form_type: "material",
        parent_ref_code: parentRefCode || undefined,
        form_data: {
          ...stripSelfSkipTransients(form),
          items: form.items.filter((it) => it.name.trim()),
          attachments,
        },
      };
      const { data } = await api.post("/requisitions/material", payload);
      // clean up draft if we were editing one
      if (currentDraftId) {
        try { await api.delete(`/requisitions/drafts/${currentDraftId}`); } catch {}
      }
      toast.success(`Submitted — ${data.ref_code}`);
      nav(`/requisitions/${data.ref_code}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const { data } = await api.post("/requisitions/draft", {
        form_type: "material",
        form_data: { ...stripSelfSkipTransients(form), attachments },
        id: currentDraftId || undefined,
      });
      setCurrentDraftId(data.id);
      toast.success("Draft saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingDraft(false);
    }
  };

  return (
    <Layout>
      <Toaster position="bottom-right" richColors />
      <div className="max-w-5xl mx-auto relative">
        <FormLogo />
        <div className="mb-4 text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">
          VGWS-MAT-2026 · Material Requisition
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight">
          Material Requisition
        </h1>
        <p className="text-sm text-[#4a524c] mt-1">Fill this sheet carefully — every field is auditable.</p>

        <div className="top-notice mt-4 px-4 py-3 text-sm" data-testid="top-notice">
          <div className="font-serif font-semibold mb-1 flex items-center gap-2">
            <AlertCircle size={16} /> Important
          </div>
          <ul className="list-disc pl-5 space-y-0.5">
            <li>Ideally, submit at least <strong>15 days</strong> prior to the requirement.</li>
            <li>At minimum, submit <strong>10 days</strong> prior — online delivery timelines can be unpredictable.</li>
          </ul>
        </div>

        <EmergencyToggle
          isEmergency={form.is_emergency}
          onToggle={(v) => setForm((f) => ({ ...f, is_emergency: v, emergency_reason: v ? f.emergency_reason : "" }))}
          emergencyReason={form.emergency_reason}
          onReasonChange={(v) => setForm((f) => ({ ...f, emergency_reason: v }))}
        />

        <form className="mt-6 space-y-6" onSubmit={onSubmit} data-testid="material-form">
          <ResubmitFromSection
            isResubmit={isResubmit}
            onToggle={(v) => { setIsResubmit(v); if (!v) setParentRefCode(null); }}
            parentRefCode={parentRefCode}
            onSelectHistory={(fd, ref) => {
              setForm((f) => ({
                ...f,
                ...fd,
                staffName: f.staffName || fd.staffName || "",
                staffEmail: f.staffEmail || fd.staffEmail || "",
                items: (fd.items && fd.items.length) ? fd.items : f.items,
              }));
              setParentRefCode(ref);
            }}
          />
          {/* Section I */}
          <Section numeral="I" title="Applicant / SPOC details">
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Name of Applicant / SPOC" required>
                <input className="input-vgws" required value={form.staffName} onChange={set("staffName")} data-testid="field-staffName" />
              </Field>
              <Field label="Mail ID of Applicant / SPOC" required>
                <input type="email" className="input-vgws" required value={form.staffEmail} onChange={set("staffEmail")} data-testid="field-staffEmail" />
              </Field>
              <Field label="Contact Number" required>
                <input className="input-vgws" required placeholder="10-digit mobile" value={form.staffContact} onChange={set("staffContact")} data-testid="field-staffContact" />
              </Field>
            </div>
          </Section>

          {/* Phase 8.3 — Self-skip radios */}
          <SelfSkipSection numeral="II·a" form={form} setField={setField} userRole={user?.role} />

          {/* Section II */}
          <Section numeral="II" title="Requested by">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Main department" required>
                <select
                  className="input-vgws" required value={form.mainDept}
                  onChange={(e) => setForm((f) => ({ ...f, mainDept: e.target.value, subDept: "" }))}
                  data-testid="field-mainDept"
                >
                  <option value="" disabled>Select department</option>
                  {depts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                </select>
              </Field>
              <Field label="Sub department" required>
                <select
                  className="input-vgws" required disabled={!subDepts.length}
                  value={form.subDept} onChange={set("subDept")}
                  data-testid="field-subDept"
                >
                  <option value="" disabled>
                    {subDepts.length ? "Select sub-department" : "Select main department first"}
                  </option>
                  {subDepts.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Select the respective HOD for consent & approval" required={!isHodDropdownOptional(form)}>
              <select className={`input-vgws ${isHodDropdownOptional(form) ? "opacity-70" : ""}`} required={!isHodDropdownOptional(form)} value={form.hodApproval} onChange={set("hodApproval")} data-testid="field-hodApproval">
                <option value="">{isHodDropdownOptional(form) ? "Optional — HOD will be skipped" : "Select HOD"}</option>
                {hods.map((h) => (
                  <option key={h.email} value={h.email}>
                    {((h.departments || []).join(", ") || "—") + " — " + h.name}
                  </option>
                ))}
              </select>
              {isHodDropdownOptional(form) ? (
                <div className="mt-1 text-[11px] leading-snug text-[#2d6b6b]" data-testid="hod-self-skip-hint">HOD approval will be skipped based on your self-declaration.</div>
              ) : null}
            </Field>
          </Section>

          {/* Section III */}
          <Section numeral="III" title="Material details">
            <Field label="Material / service required by (class, department, or wing)" required>
              <input className="input-vgws" required
                     placeholder="e.g. Grade 5 Science Lab, PYP Wing, Boys Boarding House"
                     value={form.materialRequiredBy} onChange={set("materialRequiredBy")}
                     data-testid="field-materialRequiredBy" />
            </Field>

            <Field label="Hard Product or Software Product" required>
              <div className="flex gap-2">
                {["Hard Product", "Software Product"].map((c) => (
                  <label key={c}
                         className={`px-4 py-2 rounded-md border cursor-pointer text-sm font-medium transition-all ${
                            form.materialCategory === c
                              ? "bg-[#7a1f2b] text-white border-[#5a1520]"
                              : "bg-white text-[#7a1f2b] border-[#e2d7c3] hover:border-[#7a1f2b]"
                         }`}>
                    <input type="radio" className="hidden" name="materialCategory" value={c}
                           checked={form.materialCategory === c}
                           onChange={set("materialCategory")}
                           data-testid={`field-materialCategory-${c.replace(/\s+/g, "")}`} />
                    {c}
                  </label>
                ))}
              </div>
            </Field>

            <Field label="Purpose of utility (why the material is required)" required>
              <textarea className="input-vgws min-h-[100px]" required
                        placeholder="Explain clearly why this material is needed..."
                        value={form.purposeOfUtility} onChange={set("purposeOfUtility")}
                        data-testid="field-purposeOfUtility" />
            </Field>

            <Field label="Material list" required hint="One row per item; at least one row required.">
              <div className="mb-2 text-xs italic bg-[#fcebe9] text-[#b3261e] border-l-4 border-[#b3261e] px-3 py-2 rounded"
                   data-testid="material-admin-status-note">
                <strong>Note:</strong> The "Material Status Update" column is reserved
                for Admin-1 (Bursar / CAO) official use only. Applicants cannot edit this field.
              </div>
              <div className="overflow-x-auto rounded-md border border-[#e2d7c3]">
                <table className="material-table" data-testid="material-items-table">
                  <thead>
                    <tr>
                      <th className="w-10">Sl.No</th>
                      <th>Material Name</th>
                      <th className="w-24">Quantity</th>
                      <th>Purchase link (optional)</th>
                      <th className="w-32">Approx Cost (₹)</th>
                      <th className="w-40">Material Status Update <span className="text-[10px] font-normal text-[#6e7770] block">(Admin-1 use only)</span></th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => (
                      <tr key={i}>
                        <td className="text-center text-[#7a1f2b] font-semibold">{i + 1}</td>
                        <td><input required={i === 0} placeholder="e.g. A4 paper ream"
                                   value={it.name} onChange={(e) => setItem(i, "name", e.target.value)}
                                   data-testid={`material-item-name-${i}`} /></td>
                        <td><input type="text" placeholder='e.g. "10 kg", "2 boxes"'
                                   required={!!(it.name || "").trim()}
                                   value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)}
                                   data-testid={`material-item-qty-${i}`} /></td>
                        <td><input type="url" placeholder="Product page link (optional)"
                                   value={it.link} onChange={(e) => setItem(i, "link", e.target.value)}
                                   data-testid={`material-item-link-${i}`} /></td>
                        <td><input type="number" min="0" placeholder="e.g. 500"
                                   value={it.cost} onChange={(e) => setItem(i, "cost", e.target.value)}
                                   data-testid={`material-item-cost-${i}`} /></td>
                        <td>
                          <input disabled value="—" readOnly
                                 className="!bg-[#faf5eb] !text-[#6e7770] !cursor-not-allowed text-center"
                                 data-testid={`material-admin-status-${i}`} />
                        </td>
                        <td>
                          <button type="button" className="row-del" onClick={() => rmRow(i)}
                                  disabled={form.items.length === 1}
                                  aria-label="Remove row" data-testid={`material-item-remove-${i}`}>×</button>
                        </td>
                      </tr>
                    ))}
                    {(() => {
                      const { total, hasUnparseable } = computeGrandTotal(form.items);
                      return (
                        <tr className="bg-[#faf5eb] font-semibold border-t-2 border-[#7a1f2b]"
                            data-testid="material-grand-total-row">
                          <td colSpan={4} className="text-right text-[#7a1f2b]">Grand Total</td>
                          <td className="text-[#7a1f2b]" data-testid="material-grand-total-value">
                            {hasUnparseable ? (
                              <span title="Some line items have non-numeric cost"
                                    className="cursor-help">
                                ₹{total.toFixed(2)} <span className="text-[#b3261e]">?</span>
                              </span>
                            ) : (
                              <>₹{total.toFixed(2)}</>
                            )}
                          </td>
                          <td colSpan={2}></td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <button type="button" className="btn-ghost" onClick={addRow} data-testid="add-material-row">
                  <PlusCircle size={16} /> Add row
                </button>
                <span className="text-xs text-[#6e7770]">{form.items.length} row(s)</span>
              </div>
            </Field>

            <Field label="Quality / brand / warranty / other requirements" hint="Optional">
              <textarea className="input-vgws min-h-[80px]"
                        placeholder="Preferred brand, quality grade, warranty period..."
                        value={form.qualityBrandWarranty} onChange={set("qualityBrandWarranty")}
                        data-testid="field-qualityBrandWarranty" />
            </Field>
          </Section>

          {/* Section IV */}
          <Section numeral="IV" title="Procurement">
            <Field label="Tentative date material required by" required>
              <input type="date" className="input-vgws w-full sm:w-64" required
                     value={form.requiredByDate} onChange={set("requiredByDate")}
                     data-testid="field-requiredByDate" />
            </Field>
          </Section>

          {/* Section V */}
          <Section numeral="V" title="Attachments">
            <FileField label="Material / service reference image or PDF"
                       fieldName="materialImage"
                       accept=".jpg,.jpeg,.png,.pdf"
                       attachments={attachments}
                       onChange={(e) => onUpload("materialImage", e)}
                       disabled={uploading} testid="attach-materialImage" />
            <FileField label="Attach performa invoice or price screenshot"
                       fieldName="performaInvoice"
                       accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                       attachments={attachments}
                       onChange={(e) => onUpload("performaInvoice", e)}
                       disabled={uploading} testid="attach-performaInvoice" />
          </Section>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <p className="text-xs text-[#6e7770] max-w-md">
              Fields marked <span className="text-[#b3261e]">*</span> are required. On submit, you&apos;ll receive a reference code and the selected HOD is notified.
            </p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onSaveDraft} disabled={savingDraft}
                      className="btn-ghost" data-testid="save-draft-button">
                <Save size={16} /> {savingDraft ? "Saving…" : (currentDraftId ? "Update draft" : "Save as draft")}
              </button>
              <button type="submit" disabled={submitting} className="btn-vgws" data-testid="submit-material-button">
                <Send size={16} /> {submitting ? "Submitting…" : "Submit requisition"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
}

function Section({ numeral, title, children }) {
  return (
    <section className="sheet p-5 sm:p-7" data-testid={`section-${numeral}`}>
      <div className="flex items-baseline gap-3 mb-5">
        <span className="numeral text-lg tracking-[0.2em]">{numeral}</span>
        <h2 className="font-serif text-xl font-semibold text-[#1f2320]">{title}</h2>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
        {label}
        {required && <span className="text-[#b3261e]"> *</span>}
        {hint && <span className="ml-2 text-xs font-normal text-[#6e7770]">{hint}</span>}
      </span>
      {children}
    </label>
  );
}

function FileField({ label, fieldName, accept, attachments, onChange, disabled, testid }) {
  const existing = attachments.find((a) => a.field_name === fieldName);
  return (
    <div>
      <div className="block text-sm font-medium text-[#1f2320] mb-1.5">{label}</div>
      <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-[#cbbba0] rounded-md bg-white cursor-pointer hover:border-[#7a1f2b] transition-colors" data-testid={testid}>
        <Paperclip size={16} className="text-[#7a1f2b]" />
        <span className="text-sm text-[#4a524c]">
          {existing ? `Replace: ${existing.filename}` : "Choose file (max 10 MB)"}
        </span>
        <input type="file" accept={accept} onChange={onChange} disabled={disabled} className="hidden" />
      </label>
      {existing && (
        <div className="mt-1.5 text-xs text-[#2d6b6b] flex items-center gap-2">
          <span>✔ {existing.filename}</span>
          <span className="text-[#6e7770]">({Math.round(existing.size / 1024)} KB)</span>
        </div>
      )}
    </div>
  );
}
